import React, { useState } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Flex from '@atlaskit/primitives/components/flex';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import IconButton from '@atlaskit/button/icon/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { expValEquals } from '@confluence/feature-experiments';

import { i18n } from './inlineCommentsi18n.ts';

const expandedContentStyles = xcss({
	marginTop: 'space.150',
});

// The rows carry the padding so the header can run edge to edge — see
// `danglingAnnotatedTextBodyEdgeToEdgeStyle` in `CommentBody.tsx`. The two background colours are
// the only separation; the design has no divider.
//
// Keep the header a flex container: as a block it inherits a 20px strut from the panel's
// `font.body`, which makes it 4px taller than the designed 40px.
const headerStyles = xcss({
	backgroundColor: 'color.background.neutral',
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

const deletedContentStyles = xcss({
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

interface DanglingContentProps {
	annotatedText?: string | null;
	/**
	 * Render only the header, for content types that never quote the highlighted text. Only used
	 * under `cc_comments_detached_in_open_view`.
	 */
	hideDeletedText?: boolean;
}

export const DanglingContent: React.FC<DanglingContentProps> = ({
	annotatedText,
	hideDeletedText = false,
}) => {
	const { formatMessage } = useIntl();
	const [isDanglingContentExpanded, setIsDanglingContentExpanded] = useState(false);

	if (expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true)) {
		return (
			<Box>
				<Flex xcss={headerStyles} alignItems="center" testId="dangling-content-header">
					<Text size="small" weight="medium" color="color.text">
						{formatMessage(i18n.thisContentWasDeleted)}
					</Text>
				</Flex>
				{!hideDeletedText && (
					<Box xcss={deletedContentStyles} testId="dangling-content-deleted-text">
						<Text size="small" color="color.text" maxLines={3}>
							{annotatedText}
						</Text>
					</Box>
				)}
			</Box>
		);
	}

	return (
		<Box>
			<Flex gap="space.100" alignItems="center" justifyContent="space-between">
				<Flex gap="space.100" alignItems="center">
					<Text size="small" maxLines={3} color="color.text" weight="bold">
						{formatMessage(i18n.thisContentWasDeleted)}
					</Text>
				</Flex>
				<Tooltip
					content={
						isDanglingContentExpanded
							? formatMessage(i18n.collapseDeletedContent)
							: formatMessage(i18n.viewDeletedContent)
					}
					hasNewContentOnTriggerClick
				>
					<IconButton
						appearance="subtle"
						icon={isDanglingContentExpanded ? ChevronUpIcon : ChevronDownIcon}
						label={
							isDanglingContentExpanded
								? formatMessage(i18n.collapseDeletedContent)
								: formatMessage(i18n.viewDeletedContent)
						}
						onClick={() => setIsDanglingContentExpanded(!isDanglingContentExpanded)}
						testId={
							isDanglingContentExpanded
								? 'dangling-content-toggle-collapse'
								: 'dangling-content-toggle-expand'
						}
						spacing="compact"
					/>
				</Tooltip>
			</Flex>
			{isDanglingContentExpanded && (
				<Box xcss={expandedContentStyles}>
					<Text size="small" color="color.text.subtlest" maxLines={3}>
						{annotatedText}
					</Text>
				</Box>
			)}
		</Box>
	);
};
